import React, { useMemo, useState } from 'react';
import { ArrowRightOutlined, CheckOutlined, ThunderboltFilled } from '@ant-design/icons';
import { useI18n } from '../../i18n/I18nContext';
import { useProMessages } from '../../i18n/pro';
import Reveal from './Reveal';
import CountUp from './CountUp';
import { useTilt } from './useTilt';
import './ProPricing.css';

/** Prix de la formule Pro (USD / 30 jours) et commission Standard : miroir du backend (plan.constants.ts). */
const PRO_PRICE_USD = 49.99;
const STANDARD_FEE_RATE = 0.03;
const BREAK_EVEN_USD = Math.ceil(PRO_PRICE_USD / STANDARD_FEE_RATE);

const SLIDER_MIN = 100;
const SLIDER_MAX = 20000;
const SLIDER_STEP = 100;

type Props = {
  onStartStandard: () => void;
  onStartPro: () => void;
};

const ProPricing: React.FC<Props> = ({ onStartStandard, onStartPro }) => {
  const { t, locale } = useI18n();
  const { landing: p } = useProMessages();
  const [sales, setSales] = useState(3000);
  const stdTilt = useTilt<HTMLDivElement>(4);
  const proTilt = useTilt<HTMLDivElement>(4);

  const money = useMemo(() => {
    const fmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    const fmtCents = new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
    return { whole: (n: number) => fmt.format(n), cents: (n: number) => fmtCents.format(n) };
  }, [locale]);

  const standardFees = sales * STANDARD_FEE_RATE;
  const saving = standardFees - PRO_PRICE_USD;
  const profitable = saving > 0;
  const standardItems = [t.pricing.freeAccount, t.pricing.noMonthly, t.pricing.tonWithdrawals, t.pricing.completeDashboard];
  const sliderPct = ((sales - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN)) * 100;

  return (
    <div className="ts-plans">
      <Reveal className="ts-plans-grid" y={50} scale={0.97} stagger={0.12}>
        {/* ---------- Standard ---------- */}
        <div
          ref={stdTilt.ref}
          className="ts-plan ts-tilt"
          onMouseMove={stdTilt.onMouseMove}
          onMouseLeave={stdTilt.onMouseLeave}
        >
          <div className="ts-tilt-shine" />
          <h3 className="ts-plan-name">{p.standardName}</h3>
          <p className="ts-plan-tagline">{p.standardTagline}</p>
          <div className="ts-plan-price">
            <span className="ts-plan-amount">
              <CountUp value="3%" duration={1.2} />
            </span>
            <span className="ts-plan-period">{t.pricing.perTransaction}</span>
          </div>
          <ul className="ts-plan-features">
            {standardItems.map((item) => (
              <li key={item}>
                <span className="ts-plan-check">
                  <CheckOutlined />
                </span>
                {item}
              </li>
            ))}
          </ul>
          <button type="button" className="ts-btn ts-btn--ghost ts-plan-cta" onClick={onStartStandard}>
            {p.standardCta}
            <ArrowRightOutlined />
          </button>
        </div>

        {/* ---------- Pro ---------- */}
        <div
          ref={proTilt.ref}
          className="ts-plan ts-plan--pro ts-tilt"
          onMouseMove={proTilt.onMouseMove}
          onMouseLeave={proTilt.onMouseLeave}
        >
          <div className="ts-tilt-shine" />
          <span className="ts-plan-badge">
            <ThunderboltFilled /> {p.proBadge}
          </span>
          <h3 className="ts-plan-name">{p.proName}</h3>
          <p className="ts-plan-tagline">{p.proNote}</p>
          <div className="ts-plan-price">
            <span className="ts-plan-amount ts-plan-amount--pro">{p.proPrice}</span>
            <span className="ts-plan-period">{p.proPeriod}</span>
          </div>
          <ul className="ts-plan-features">
            {p.proFeatures.map((item) => (
              <li key={item}>
                <span className="ts-plan-check">
                  <CheckOutlined />
                </span>
                {item}
              </li>
            ))}
          </ul>
          <button type="button" className="ts-btn ts-btn--primary ts-plan-cta" onClick={onStartPro}>
            {p.proCta}
            <ArrowRightOutlined />
          </button>
          <p className="ts-plan-pay">{p.payWith}</p>
        </div>
      </Reveal>

      {/* ---------- 3 étapes ---------- */}
      <Reveal className="ts-plans-steps" y={40}>
        <h3 className="ts-plans-subtitle">{p.stepsTitle}</h3>
        <ol className="ts-plans-steps-list">
          {p.steps.map((step, i) => (
            <li key={step.title}>
              <span className="ts-plans-step-num">{i + 1}</span>
              <div>
                <strong>{step.title}</strong>
                <p>{step.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </Reveal>

      {/* ---------- Calculateur d'économies ---------- */}
      <Reveal className="ts-calc" y={40}>
        <h3 className="ts-plans-subtitle">{p.calcTitle}</h3>
        <div className="ts-calc-body">
          <div className="ts-calc-input">
            <div className="ts-calc-input-head">
              <label htmlFor="ts-calc-range">{p.calcSalesLabel}</label>
              <strong>{money.whole(sales)}</strong>
            </div>
            <input
              id="ts-calc-range"
              className="ts-calc-range"
              type="range"
              min={SLIDER_MIN}
              max={SLIDER_MAX}
              step={SLIDER_STEP}
              value={sales}
              style={{ ['--pct' as any]: `${sliderPct}%` }}
              onChange={(e) => setSales(Number(e.target.value))}
            />
          </div>
          <div className="ts-calc-results">
            <div className="ts-calc-row">
              <span>{p.calcStandard}</span>
              <strong>{money.cents(standardFees)}</strong>
            </div>
            <div className="ts-calc-row">
              <span>{p.calcPro}</span>
              <strong>{money.cents(PRO_PRICE_USD)}</strong>
            </div>
            <div className={`ts-calc-row ts-calc-row--total ${profitable ? 'is-positive' : ''}`}>
              <span>{p.calcSaving}</span>
              <strong>{profitable ? money.cents(saving) : money.cents(0)}</strong>
            </div>
          </div>
        </div>
        <p className="ts-calc-note">
          {(profitable ? p.calcBreakEven : p.calcBelow).replace('{amount}', money.whole(BREAK_EVEN_USD))}
        </p>
      </Reveal>
    </div>
  );
};

export default ProPricing;
